import { useCallback, useMemo, useRef } from "react";
import {
  DateRangeSettings,
  defaultDateRangeSettings,
  toStoredDate,
  toStoredIso,
} from "./dateRangeSettings";
import { useUserScopedSettings } from "./useUserScopedSettings";

export interface PersistedDateRangeOptions {
  /** Names the range, e.g. `faultMonitor.dateRange`. Must be stable. */
  scope: string;
  /** How many days back the range reaches when the user has not picked one yet. */
  defaultLookbackDays: number;
}

/**
 * A date range that follows the user rather than the browser.
 *
 * Returns the same shape the pages' own date hooks used, so a page only changes where it says
 * which range it means.
 */
export function usePersistedDateRange({ scope, defaultLookbackDays }: PersistedDateRangeOptions) {
  // Computed once: a range relative to "now" must not shift under the user while they read it.
  const defaultsRef = useRef(defaultDateRangeSettings(defaultLookbackDays));
  const { settings, setSettings, isFetched, isSaving } = useUserScopedSettings<DateRangeSettings>(
    scope,
    defaultsRef.current,
  );

  const fromDate = useMemo(
    () => toStoredDate(settings.fromDateIso, new Date(defaultsRef.current.fromDateIso)),
    [settings.fromDateIso],
  );
  const toDate = useMemo(
    () => toStoredDate(settings.toDateIso, new Date(defaultsRef.current.toDateIso)),
    [settings.toDateIso],
  );

  const setFromDate = useCallback(
    (next: Date) => {
      const iso = toStoredIso(next);
      if (iso === null) {
        return;
      }
      setSettings(current => ({ ...current, fromDateIso: iso }));
    },
    [setSettings],
  );

  const setToDate = useCallback(
    (next: Date) => {
      const iso = toStoredIso(next);
      if (iso === null) {
        return;
      }
      setSettings(current => ({ ...current, toDateIso: iso }));
    },
    [setSettings],
  );

  return { fromDate, toDate, setFromDate, setToDate, isFetched, isSaving };
}
